import axios from "axios";

// ============================================================
// CONFIGURACIÓN DEL CLIENTE API - CRM HITPOLY
// ============================================================
// Este archivo centraliza todas las llamadas a la API del backend
// Backend: https://apiformark.hitpoly.com/ajax/
// ============================================================

const API_BASE = import.meta.env.DEV
  ? "/ajax/"
  : "https://apiformark.hitpoly.com/ajax/";

export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
  transformResponse: [
    (data) => {
      if (typeof data !== "string") return data;
      try {
        return JSON.parse(data);
      } catch {
        const jsonMatch = data.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[0]);
          } catch {
            return data;
          }
        }
        return data;
      }
    },
  ],
});

const getAxiosErrorMessage = (error) => {
  const responseMessage = error?.response?.data?.message;
  const fallback = error?.message || "Error de red inesperado";
  return responseMessage || fallback;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const profileId = localStorage.getItem("crm_active_profile_id");
    if (profileId) {
      config.headers["X-Profile-ID"] = profileId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const responseData = error?.response?.data;
    const responseStatus = error?.response?.status;
    const requestUrl = error?.config?.url;
    const requestData = error?.config?.data;

    if (responseData && typeof responseData === 'object' && responseData.success === false) {
      return Promise.resolve({ data: responseData });
    }

    if (responseStatus === 401 && (!responseData || responseData === '')) {
      return Promise.resolve({
        data: {
          success: false,
          message: 'Se requiere autenticación con Google',
          error_type: 'INSUFFICIENT_SCOPES',
          authUrl: 'https://apiweb.hitpoly.com/ajax/GoogleAuthController.php?accion=auth',
        },
      });
    }

    return Promise.reject(new Error(getAxiosErrorMessage(error)));
  },
);

export const postJson = async (endpoint, payload = {}, config = {}) => {
  const { data } = await apiClient.post(endpoint, payload, config);
  return data;
};

export const postText = async (endpoint, payload = {}, config = {}) => {
  const { data } = await apiClient.post(endpoint, payload, {
    ...config,
    responseType: "text",
    transformResponse: [(raw) => raw],
  });

  return data;
};

// ============================================================
// FUNCIÓN PRINCIPAL: Backend Modular del CRM
// ============================================================
// Usa el patrón module/action/data para llamar a cualquier endpoint
// Ejemplo: apiCall("prospects", "list", { company_id: 1 })
// ============================================================
export const apiCall = async (module, action, payload = {}) => {
  const profileId = localStorage.getItem("crm_active_profile_id");
  let profileType = localStorage.getItem("crm_active_profile_type");
  const userId = localStorage.getItem("crm_user_id");
  if (profileId) {
    if (!profileType) profileType = 'empresa';
    payload = { ...payload, profile_id: parseInt(profileId, 10), profile_type: profileType };
  }
  if (userId) {
    payload = { ...payload, user_id: parseInt(userId, 10) };
  }

  const requestBody = {
    module,
    action,
    data: payload
  };

  const { data } = await apiClient.post("index.php", requestBody);
  return data;
};

// ============================================================
// HELPER: Obtener company_id efectivo seg	n el perfil activo
// ============================================================
// Para perfil empresa: usa activeCompany.id o fallback a activeProfile.id
// Para perfil main (sin compa		a): retorna null (apiClient auto-agrega profile_id)
// ============================================================
export const getEffectiveCompanyId = (activeCompany, activeProfile) => {
  return activeCompany?.id || (activeProfile?.profile_type === 'empresa' ? activeProfile?.id : null);
};

// ============================================================
// FUNCIONES ESPECÍFICAS POR MÓDULO
// ============================================================
// Estas funciones facilitan el uso de la API con métodos
// predefinidos para cada módulo del sistema
// ============================================================

// --- USUARIOS ---
export const usersApi = {
  list: (data) => apiCall("users", "list", data),
  get: (data) => apiCall("users", "get", data),
  syncLegacy: (data) => apiCall("users", "sync_legacy", data),
};

// --- EMPRESAS ---
export const companiesApi = {
  list: (data) => apiCall("companies", "list", data),
  get: (data) => apiCall("companies", "get", data),
  create: (data) => apiCall("companies", "create", data),
  update: (data) => apiCall("companies", "update", data),
};

// --- CUENTAS ---
export const accountsApi = {
  listByCompany: (data) => apiCall("accounts", "list_by_company", data),
  get: (data) => apiCall("accounts", "get", data),
  create: (data) => apiCall("accounts", "create", data),
  update: (data) => apiCall("accounts", "update", data),
  delete: (data) => apiCall("accounts", "delete", data),
  setDefault: (data) => apiCall("accounts", "set_default", data),
};

// --- EQUIPO ---
export const teamApi = {
  listByCompany: (data) => apiCall("team", "list_by_company", data),
  add: (data) => apiCall("team", "add", data),
  update: (data) => apiCall("team", "update", data),
};

// --- ROLES ---
export const rolesApi = {
  list: (data) => apiCall("roles", "list", data),
  get: (data) => apiCall("roles", "get", data),
  create: (data) => apiCall("roles", "create", data),
  update: (data) => apiCall("roles", "update", data),
  delete: (data) => apiCall("roles", "delete", data),
  assignPermissions: (data) => apiCall("roles", "assign_permissions", data),
  listByModule: (data) => apiCall("roles", "list_by_module", data),
};

// --- PERMISOS ---
export const permissionsApi = {
  list: (data) => apiCall("permissions", "list", data),
  get: (data) => apiCall("permissions", "get", data),
  create: (data) => apiCall("permissions", "create", data),
  update: (data) => apiCall("permissions", "update", data),
  delete: (data) => apiCall("permissions", "delete", data),
  listByModule: (data) => apiCall("permissions", "list_by_module", data),
  listAllModules: (data) => apiCall("permissions", "list_all_modules", data),
  listByRole: (data) => apiCall("permissions", "list_by_role", data),
  listByMember: (data) => apiCall("permissions", "list_by_member", data),
  checkPermission: (data) => apiCall("permissions", "check_permission", data),
};

// --- PROSPECTOS ---
export const prospectsApi = {
  list: (data) => apiCall("prospects", "list", data),
  get: (data) => apiCall("prospects", "get", data),
  create: (data) => apiCall("prospects", "create", data),
  update: (data) => apiCall("prospects", "update", data),
  delete: (data) => apiCall("prospects", "delete", data),
};

// --- NEGOCIOS ---
export const businessesApi = {
  listByCompany: (data) => apiCall("businesses", "list_by_company", data),
  get: (data) => apiCall("businesses", "get", data),
  create: (data) => apiCall("businesses", "create", data),
  update: (data) => apiCall("businesses", "update", data),
};

// --- LISTAS ---
export const listsApi = {
  listByCompany: (data) => apiCall("lists", "list_by_company", data),
  get: (data) => apiCall("lists", "get", data),
  create: (data) => apiCall("lists", "create", data),
  update: (data) => apiCall("lists", "update", data),
  delete: (data) => apiCall("lists", "delete", data),
  addProspect: (data) => apiCall("lists", "add_prospect", data),
  removeProspect: (data) => apiCall("lists", "remove_prospect", data),
};

// --- PRODUCTOS ---
export const productsApi = {
  listByCompany: (data) => apiCall("products", "list_by_company", data),
  get: (data) => apiCall("products", "get", data),
};

// --- ETAPAS DE NEGOCIO ---
export const stagesApi = {
  list: (data) => apiCall("business_stages", "list", data),
  create: (data) => apiCall("business_stages", "create", data),
  update: (data) => apiCall("business_stages", "update", data),
  delete: (data) => apiCall("business_stages", "delete", data),
};

// --- FORMULARIOS ---
export const formsApi = {
  listByCompany: (data) => apiCall("forms", "list_by_company", data),
  get: (data) => apiCall("forms", "get", data),
  submit: (data) => apiCall("forms", "submit", data),
};

// --- COTIZACIONES ---
export const quotationsApi = {
  listByCompany: (data) => apiCall("quotations", "list_by_company", data),
  get: (data) => apiCall("quotations", "get", data),
  create: (data) => apiCall("quotations", "create", data),
  update: (data) => apiCall("quotations", "update", data),
  approve: (data) => apiCall("quotations", "approve", data),
  reject: (data) => apiCall("quotations", "reject", data),
  convertToContract: (data) => apiCall("quotations", "convert_to_contract", data),
};

// --- CONTRATOS ---
export const contractsApi = {
  listByBusiness: (data) => apiCall("contracts", "list_by_business", data),
  get: (data) => apiCall("contracts", "get", data),
  sign: (data) => apiCall("contracts", "sign", data),
};

// --- CHECKOUT ---
export const checkoutApi = {
  listByBusiness: (data) => apiCall("checkout", "list_by_business", data),
  get: (data) => apiCall("checkout", "get", data),
  create: (data) => apiCall("checkout", "create", data),
};

// --- PAGOS ---
export const paymentsApi = {
  listByBusiness: (data) => apiCall("payments", "list_by_business", data),
  create: (data) => apiCall("payments", "create", data),
};

// --- CORREO ---
export const emailCampaignsApi = {
  listByCompany: (data) => apiCall("email_campaigns", "list_by_company", data),
  create: (data) => apiCall("email_campaigns", "create", data),
  update: (data) => apiCall("email_campaigns", "update", data),
  checkSmtp: (data) => apiCall("email_campaigns", "check_smtp", data),
  saveSmtp: (data) => apiCall("email_campaigns", "save_smtp", data),
};

export const emailsApi = {
  listByParent: (data) => apiCall("emails", "list_by_parent", data),
  create: (data) => apiCall("emails", "create", data),
};

export const emailBroadcastsApi = {
  listByCompany: (data) => apiCall("email_broadcasts", "list_by_company", data),
  get: (data) => apiCall("email_broadcasts", "get", data),
  create: (data) => apiCall("email_broadcasts", "create", data),
  send: (data) => apiCall("email_broadcasts", "send", data),
  delete: (data) => apiCall("email_broadcasts", "delete", data),
  checkSmtp: (data) => apiCall("email_broadcasts", "check_smtp", data),
  saveSmtp: (data) => apiCall("email_broadcasts", "save_smtp", data),
};

export const emailSequencesApi = {
  listByCompany: (data) => apiCall("email_sequences", "list_by_company", data),
  get: (data) => apiCall("email_sequences", "get", data),
  create: (data) => apiCall("email_sequences", "create", data),
  update: (data) => apiCall("email_sequences", "update", data),
  delete: (data) => apiCall("email_sequences", "delete", data),
  execute: (data) => apiCall("email_sequences", "execute", data),
};

// --- REUNIONES ---
export const meetingsApi = {
  listByCompany: (data) => apiCall("meetings", "list_by_company", data),
  create: (data) => apiCall("meetings", "create", data),
};

// --- NOTAS ---
export const notesApi = {
  listByParent: (data) => apiCall("notes", "list_by_parent", data),
  create: (data) => apiCall("notes", "create", data),
  update: (data) => apiCall("notes", "update", data),
  delete: (data) => apiCall("notes", "delete", data),
};

// --- TAREAS ---
export const tasksApi = {
  list: (data) => apiCall("tasks", "list", data),
  get: (data) => apiCall("tasks", "get", data),
  create: (data) => apiCall("tasks", "create", data),
  update: (data) => apiCall("tasks", "update", data),
  delete: (data) => apiCall("tasks", "delete", data),
  complete: (data) => apiCall("tasks", "complete", data),
  listByProspect: (data) => apiCall("tasks", "list_by_prospect", data),
  listByBusiness: (data) => apiCall("tasks", "list_by_business", data),
  listByUser: (data) => apiCall("tasks", "list_by_user", data),
};

// --- CONVERSACIONES ---
export const conversationsApi = {
  list: (data) => apiCall("conversations", "list", data),
  get: (data) => apiCall("conversations", "get", data),
  create: (data) => apiCall("conversations", "create", data),
  update: (data) => apiCall("conversations", "update", data),
  close: (data) => apiCall("conversations", "close", data),
  assign: (data) => apiCall("conversations", "assign", data),
  listByProspect: (data) => apiCall("conversations", "list_by_prospect", data),
  listByBusiness: (data) => apiCall("conversations", "list_by_business", data),
  listUnassigned: (data) => apiCall("conversations", "list_unassigned", data),
};

// --- MENSAJES ---
export const messagesApi = {
  list: (data) => apiCall("messages", "list", data),
  get: (data) => apiCall("messages", "get", data),
  create: (data) => apiCall("messages", "create", data),
  update: (data) => apiCall("messages", "update", data),
  delete: (data) => apiCall("messages", "delete", data),
  markDelivered: (data) => apiCall("messages", "mark_delivered", data),
  markRead: (data) => apiCall("messages", "mark_read", data),
  listByProspect: (data) => apiCall("messages", "list_by_prospect", data),
  listByBusiness: (data) => apiCall("messages", "list_by_business", data),
  search: (data) => apiCall("messages", "search", data),
};

// --- CHAT EXTERNO ---
export const externalChatApi = {
  listSessions: (data) => apiCall("external_chat", "list_sessions", data),
  listMessages: (data) => apiCall("external_chat", "list_messages", data),
  createSession: (data) => apiCall("external_chat", "create_session", data),
  send: (data) => apiCall("external_chat", "send", data),
};

// --- WHATSAPP ---
export const whatsappApi = {
  getConnection: (data) => apiCall("whatsapp", "get_connection", data),
  getQR: (data) => apiCall("whatsapp", "get_qr", data),
  connect: (data) => apiCall("whatsapp", "connect", data),
  disconnect: (data) => apiCall("whatsapp", "disconnect", data),
  syncTemplates: (data) => apiCall("whatsapp", "sync_templates", data),
  listConversations: (data) => apiCall("whatsapp", "list_conversations", data),
  listTemplates: (data) => apiCall("whatsapp", "list_templates", data),
  getTemplate: (data) => apiCall("whatsapp", "get_template", data),
  createTemplate: (data) => apiCall("whatsapp", "create_template", data),
  updateTemplate: (data) => apiCall("whatsapp", "update_template", data),
  sendMessage: (data) => apiCall("whatsapp", "send_message", data),
  webhook: (data) => apiCall("whatsapp", "webhook", data),
  getStats: (data) => apiCall("whatsapp", "get_stats", data),
};

// --- HORARIOS ---
export const schedulesApi = {
  list: (data) => apiCall("schedules", "list", data),
  create: (data) => apiCall("schedules", "create", data),
  update: (data) => apiCall("schedules", "update", data),
  delete: (data) => apiCall("schedules", "delete", data),
  availableSlots: (data) => apiCall("schedules", "available_slots", data),
};

// --- ASISTENCIA ---
export const attendanceApi = {
  clockIn: (data) => apiCall("attendance", "clock_in", data),
  heartbeat: (data) => apiCall("attendance", "heartbeat", data),
  clockOut: (data) => apiCall("attendance", "clock_out", data),
};

// --- ADS ---
export const adsApi = {
  listHolders: (data) => apiCall("ads", "list_holders", data),
  createConnection: (data) => apiCall("ads", "create_connection", data),
  registerLead: (data) => apiCall("ads", "register_lead", data),
};

// --- AUTOMATIZACIONES ---
export const automationsApi = {
  list: (data) => apiCall("automations", "list", data),
  create: (data) => apiCall("automations", "create", data),
  trigger: (data) => apiCall("automations", "trigger", data),
};

// --- NOTIFICACIONES ---
export const notificationsApi = {
  listByCompany: (data) => apiCall("notifications", "list_by_company", data),
  listByMember: (data) => apiCall("notifications", "list_by_member", data),
  markRead: (data) => apiCall("notifications", "mark_read", data),
  markAllAsRead: (data) => apiCall("notifications", "mark_all_as_read", data),
};

// --- FUNNEL ---
export const funnelApi = {
  listStages: (data) => apiCall("funnel", "list_stages", data),
  trackEvent: (data) => apiCall("funnel", "track_event", data),
  getMetrics: (data) => apiCall("funnel", "get_metrics", data),
};

// --- MÉTRICAS ---
export const metricsApi = {
  userSnapshots: (data) => apiCall("metrics", "user_snapshots", data),
  funnelSnapshots: (data) => apiCall("metrics", "funnel_snapshots", data),
  salesMetrics: (data) => apiCall("metrics", "sales_metrics", data),
};

// --- ACTUALIZACIONES ---
export const updatesApi = {
  listByCompany: (data) => apiCall("updates", "list_by_company", data),
  create: (data) => apiCall("updates", "create", data),
};

// --- OBJETOS ASOCIADOS ---
export const associatedObjectsApi = {
  listByParent: (data) => apiCall("associated_objects", "list_by_parent", data),
  create: (data) => apiCall("associated_objects", "create", data),
};

// --- PERFILES ---
export const profilesApi = {
  list: (data) => apiCall("profiles", "list", data),
  get: (data) => apiCall("profiles", "get", data),
  listAll: (data) => apiCall("profiles", "list_all", data),
  relate: (data) => apiCall("profiles", "relate", data),
  unrelate: (data) => apiCall("profiles", "unrelate", data),
  relations: (data) => apiCall("profiles", "relations", data),
};

// --- EMPRESAS DE PROSPECTO ---
export const prospectCompaniesApi = {
  listByProspect: (data) => apiCall("prospect_companies", "list_by_prospect", data),
  create: (data) => apiCall("prospect_companies", "create", data),
  delete: (data) => apiCall("prospect_companies", "delete", data),
};

// --- PROFILE ROLE ASSIGNMENTS (Setter/Closer por perfil) ---
export const profileAssignmentsApi = {
  getByEntity: (data) => apiCall("profile_assignments", "get_by_entity", data),
  set: (data) => apiCall("profile_assignments", "set", data),
  remove: (data) => apiCall("profile_assignments", "remove", data),
};

// --- EMPRESA EQUIPO (Bolsa de Profesionales) ---
export const empresaEquipoApi = {
  listPending: (data) => apiCall("empresa_equipo", "list_pending", data),
  acceptMember: (data) => apiCall("empresa_equipo", "accept_member", data),
  unlinkMember: (data) => apiCall("empresa_equipo", "unlink_member", data),
  adminListAll: (data) => apiCall("empresa_equipo", "admin_list_all", data),
  listCompanyAccounts: (data) => apiCall("empresa_equipo", "list_company_accounts", data),
};

// --- GOOGLE DRIVE ---
export const driveApi = {
  listFiles: (data) => apiCall("drive", "list_files", data),
  download: (data) => apiCall("drive", "download", data),
};

// --- RELACIONES DE PROSPECTO (negocios, tickets, enlaces, contactos) ---
export const negociacionesApi = {
  listByProspect: (data) => apiCall("negociaciones", "list_by_prospect", data),
  update: (data) => apiCall("negociaciones", "update", data),
  delete: (data) => apiCall("negociaciones", "delete", data),
  searchProspects: (data) => apiCall("negociaciones", "search_prospects", data),
  linkContact: (data) => apiCall("negociaciones", "link_contact", data),
  createAndLinkContact: (data) => apiCall("negociaciones", "create_and_link_contact", data),
};

// --- NEGOCIOS (tabla independiente) ---
export const negociosApi = {
  listByProspect: (data) => apiCall("negocios", "list_by_prospect", data),
  listByCompany: (data) => apiCall("negocios", "list_by_company", data),
  get: (data) => apiCall("negocios", "get", data),
  create: (data) => apiCall("negocios", "create", data),
  update: (data) => apiCall("negocios", "update", data),
  delete: (data) => apiCall("negocios", "delete", data),
};

// --- TICKETS (tabla independiente) ---
export const ticketsApi = {
  listByProspect: (data) => apiCall("tickets", "list_by_prospect", data),
  create: (data) => apiCall("tickets", "create", data),
  update: (data) => apiCall("tickets", "update", data),
  delete: (data) => apiCall("tickets", "delete", data),
};

// --- ENLACES (tabla independiente) ---
export const enlacesApi = {
  listByProspect: (data) => apiCall("enlaces", "list_by_prospect", data),
  create: (data) => apiCall("enlaces", "create", data),
  update: (data) => apiCall("enlaces", "update", data),
  delete: (data) => apiCall("enlaces", "delete", data),
};

// --- PROSPECT CONTROLLER (endpoints directos) ---
export const prospectControllerApi = {
  listOwners: () => apiClient.get("prospectController.php", { params: { action: "listOwners" } }).then(r => r.data),
  saveProspect: (payload) => apiClient.post("prospectController.php", { action: "saveProspect", ...payload }).then(r => r.data),
};